import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  User as UserIcon,
  KeyRound,
  ShieldCheck,
  Mail,
  Eye,
  EyeOff,
  Loader2,
  PenLine,
} from "lucide-react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signature, setSignature] = useState("");
  const [savingSig, setSavingSig] = useState(false);

  // Hydrate the signature textarea whenever auth state lands.
  useEffect(() => {
    setSignature(user?.signature || "");
  }, [user?.signature, user?.id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!current || !next) return toast.error("Please fill both password fields");
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New password and confirm do not match");
    if (next === current) return toast.error("New password must be different from current");
    setSaving(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  const saveSignature = async () => {
    setSavingSig(true);
    try {
      await api.patch("/me/profile", { signature });
      await refresh?.();
      toast.success("Signature saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save signature");
    } finally {
      setSavingSig(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8 max-w-2xl" data-testid="profile-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </Link>

      <header>
        <h1 className="text-3xl font-semibold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
          <UserIcon className="w-7 h-7 text-sky-400" /> Profile
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Your account details and security settings.</p>
      </header>

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="label-overline mb-3">Account</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Name</div>
            <div className="text-sm text-zinc-200">{user.name || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</div>
            <div className="text-sm text-zinc-200">{user.email}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">EMP ID</div>
            <div className="text-sm font-mono text-zinc-200">{user.code || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Role</div>
            <div className="text-sm text-zinc-200 capitalize">{(user.role || "team_associate").replace(/_/g, " ")}</div>
          </div>
        </div>
      </section>

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid="profile-signature">
        <div className="flex items-center gap-2 mb-3">
          <PenLine className="w-4 h-4 text-sky-400" />
          <div className="label-overline">Email Signature</div>
        </div>
        <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
          Appended to every outgoing email sent when you reply on a ticket.
          Keep it short — name, designation, contact. Plain text only.
        </p>
        <Textarea
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          maxLength={2000}
          rows={6}
          placeholder={`e.g.\nThanks,\n${user.name || "Your Name"}\n${user.email || ""}\nOps Console`}
          className="bg-zinc-950 border-white/10 text-zinc-100 font-mono text-sm"
          data-testid="profile-signature-textarea"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="text-[11px] text-zinc-500" data-testid="profile-signature-counter">
            {signature.length} / 2000 characters
          </span>
          <Button
            type="button"
            onClick={saveSignature}
            disabled={savingSig || signature === (user.signature || "")}
            className="bg-sky-500 text-black hover:bg-sky-400 font-medium"
            data-testid="profile-signature-save"
          >
            {savingSig && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Save Signature
          </Button>
        </div>
      </section>

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-amber-400" />
          <div className="label-overline">Change Password</div>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Use a minimum of 8 characters. You&apos;ll stay signed in after the change.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs text-zinc-400">Current Password</Label>
            <div className="relative mt-1.5">
              <Input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="bg-zinc-950 border-white/10 text-white pr-10"
                placeholder="••••••••"
                autoComplete="current-password"
                data-testid="profile-current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">New Password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showNext ? "text" : "password"}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="bg-zinc-950 border-white/10 text-white pr-10"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  data-testid="profile-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNext((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  tabIndex={-1}
                >
                  {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Confirm New Password</Label>
              <Input
                type={showNext ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-zinc-950 border-white/10 text-white mt-1.5"
                placeholder="Re-enter new password"
                autoComplete="new-password"
                data-testid="profile-confirm-password"
              />
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <Button
              type="submit"
              disabled={saving || !current || !next || !confirm}
              className="bg-amber-500 text-black hover:bg-amber-400 font-medium"
              data-testid="profile-save-password"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Update Password
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
